import React, { useEffect, useState } from 'react'
import useGetItems from '../../fetches/useGetItems.js'
import './cardProject.css'

// Функция для преобразования Buffer данных в URL изображения
const bufferToImageUrl = (bufferData) => {
  if (!bufferData) return null;
  
  try {
    // Если это уже строка (URL или base64)
    if (typeof bufferData === 'string') {
      if (bufferData.startsWith('http') || bufferData.startsWith('data:image')) {
        return bufferData;
      }
      return null;
    }
    
    // Если это объект Buffer с полем data (массив чисел)
    if (bufferData && bufferData.data && Array.isArray(bufferData.data)) {
      // Определяем MIME тип по первым байтам
      let mimeType = 'image/png';
      if (bufferData.data.length >= 4) {
        // PNG
        if (bufferData.data[0] === 137 && bufferData.data[1] === 80) {
          mimeType = 'image/png';
        }
        // JPEG
        else if (bufferData.data[0] === 255 && bufferData.data[1] === 216) {
          mimeType = 'image/jpeg';
        }
      }
      
      const uint8Array = new Uint8Array(bufferData.data);
      const blob = new Blob([uint8Array], { type: mimeType });
      return URL.createObjectURL(blob);
    }
    
    return null;
  } catch (error) {
    console.error('Error converting buffer:', error);
    return null;
  }
};

// Хук для получения и преобразования изображений проекта
const useProjectImages = (projectId) => {
  const { data: imagesData, loading, error } = useGetItems(`http://localhost:3001/photos/${projectId}`);
  const [imageUrls, setImageUrls] = useState([]);
  const [cleanupFunctions, setCleanupFunctions] = useState([]);

  console.log(imagesData.images)

  useEffect(() => {
    if (imagesData) {
      console.log(`[useProjectImages ${projectId}] Received data:`, {
        type: typeof imagesData,
        isArray: Array.isArray(imagesData),
        keys: imagesData ? Object.keys(imagesData) : [],
        hasRows: imagesData?.rows !== undefined,
        rowsIsArray: Array.isArray(imagesData?.rows)
      });
      
      let urls = [];
      let cleanups = [];
      
      // Вариант 1: Данные - это массив фотографий
      if (Array.isArray(imagesData.images)) {
        console.log(`[useProjectImages ${projectId}] Processing array of ${imagesData.length} items`);
        
        imagesData.images.forEach((item, index) => {
          // Проверяем разные структуры объектов
          let imageBuffer = null;
          
          if (item && item.dataUrl) {
            imageBuffer = item.dataUrl;
          } else if (item && item.data) {
            imageBuffer = item;
          } else {
            imageBuffer = item;
          }
          
          const url = bufferToImageUrl(imageBuffer);
          if (url) {
            urls.push(url);
            if (url.startsWith('blob:')) {
              cleanups.push(() => URL.revokeObjectURL(url));
            }
          }
        });
      }
      // Вариант 2: Данные - это объект с полем rows (часто используется в PostgreSQL)
      else if (imagesData && imagesData.rows && Array.isArray(imagesData.rows)) {
        console.log(`[useProjectImages ${projectId}] Processing rows array of ${imagesData.rows.length} items`);
        
        imagesData.rows.forEach((row, index) => {
          if (row && row.file_data) {
            const url = bufferToImageUrl(row.file_data);
            if (url) {
              urls.push(url);
              if (url.startsWith('blob:')) {
                cleanups.push(() => URL.revokeObjectURL(url));
              }
            }
          }
        });
      }
      // Вариант 3: Данные - это один объект с file_data
      else if (imagesData && imagesData.file_data) {
        console.log(`[useProjectImages ${projectId}] Processing single image object`);
        const url = bufferToImageUrl(imagesData.file_data);
        if (url) {
          urls.push(url);
          if (url.startsWith('blob:')) {
            cleanups.push(() => URL.revokeObjectURL(url));
          }
        }
      }
      // Вариант 4: Данные - это массив чисел (прямой Buffer)
      else if (imagesData && Array.isArray(imagesData)) {
        console.log(`[useProjectImages ${projectId}] Processing direct array`);
        const url = bufferToImageUrl(imagesData);
        if (url) {
          urls.push(url);
          if (url.startsWith('blob:')) {
            cleanups.push(() => URL.revokeObjectURL(url));
          }
        }
      }
      // Вариант 5: Данные - это объект с полем data
      else if (imagesData && imagesData.data) {
        console.log(`[useProjectImages ${projectId}] Processing object with data field`);
        const url = bufferToImageUrl(imagesData);
        if (url) {
          urls.push(url);
          if (url.startsWith('blob:')) {
            cleanups.push(() => URL.revokeObjectURL(url));
          }
        }
      }
      else {
        console.warn(`[useProjectImages ${projectId}] Unknown data format:`, imagesData);
      }
      
      console.log(`[useProjectImages ${projectId}] Generated ${urls.length} URLs`);
      setImageUrls(urls);
      setCleanupFunctions(cleanups);
    }
    
    // Очистка при размонтировании
    return () => {
      cleanupFunctions.forEach(cleanup => cleanup());
    };
  }, [imagesData, projectId]);

  return { images: imageUrls, loading, error };
};

// Функция для нормализации изображений
export const normalizeImages = (imagesData) => {
  if (!imagesData) return [];
  
  const urls = [];
  
  // Обрабатываем разные форматы
  if (Array.isArray(imagesData)) {
    imagesData.forEach(item => {
      const url = bufferToImageUrl(item);
      if (url) urls.push(url);
    });
  } else if (imagesData && imagesData.rows && Array.isArray(imagesData.rows)) {
    imagesData.rows.forEach(row => {
      const url = bufferToImageUrl(row.file_data);
      if (url) urls.push(url);
    });
  } else {
    const url = bufferToImageUrl(imagesData);
    if (url) urls.push(url);
  }
  
  return urls;
};

export const ProjectGallery = ({ images, title }) => {
  const [current, setCurrent] = useState(0);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  const minSwipeDistance = 50;

  const showPrev = (e) => {
    if (e) e.stopPropagation();
    setCurrent((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const showNext = (e) => {
    if (e) e.stopPropagation();
    setCurrent((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  const handleDotClick = (index, e) => {
    e.stopPropagation();
    setCurrent(index);
  };

  const handleCarouselClick = (e) => {
    e.stopPropagation();
  };

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    if (isLeftSwipe) {
      showNext();
    }
    if (isRightSwipe) {
      showPrev();
    }
  };

  if (!images || images.length === 0) {
    return null;
  }

  return (
    <div 
      className="project-carousel" 
      onClick={handleCarouselClick}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      <div
        className="project-carousel__viewport"
        style={{ '--media-bg': `url(${images[current]})` }}
      >
        <img
          src={images[current]}
          alt={`${title} — визуализация ${current + 1}`}
          className="project-carousel__image"
          loading="lazy"
          decoding="async"
        />
      </div>

      {images.length > 1 ? (
        <>
          <button
            type="button"
            className="project-carousel__control project-carousel__control--prev"
            onClick={showPrev}
            aria-label="Предыдущее изображение"
          >
            ‹
          </button>
          <button
            type="button"
            className="project-carousel__control project-carousel__control--next"
            onClick={showNext}
            aria-label="Следующее изображение"
          >
            ›
          </button>

          <div className="project-carousel__dots" role="tablist">
            {images.map((_, index) => (
              <button
                key={index}
                type="button"
                role="tab"
                aria-selected={current === index}
                className={`project-carousel__dot ${
                  current === index ? 'is-active' : ''
                }`}
                onClick={(e) => handleDotClick(index, e)}
              />
            ))}
          </div>
        </>
      ) : null}
    </div>
  );
};

const CardProject = ({
  idProject,
  nameProject = 'Проект',
  descProject = '',
  location,
  year,
  index = 0,
  onProjectClick
}) => {
  // Используем хук для получения изображений проекта
  const { images: media, loading, error } = useProjectImages(idProject);
  
  const cover = media[0];
  const hasGallery = media.length > 1;
  const layoutClass = index % 2 === 1 ? 'wrapper wrapper--reverse' : 'wrapper';
  
  const handleCardClick = (e) => {
    // Не открываем модальное окно, если клик был на карусели
    if (e.target.closest('.project-carousel')) {
      return;
    }
    if (onProjectClick) {
      onProjectClick({
        name: nameProject,
        description: descProject,
        images: media,
        location,
        year
      });
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleCardClick();
    }
  };

  // Показываем состояние загрузки
  if (loading) {
    return (
      <div className={layoutClass}>
        <div className="cardProject_slider_main">
          <div className="cardProject_Content cardProject_Content--loading">
            <div className="loading-spinner"></div>
            <span>Загрузка изображений...</span>
          </div>
        </div>
        <div className="text-box">
          <div className="text-box__meta">
            {year ? <span>{year}</span> : null}
            {year && location ? <span>•</span> : null}
            {location ? <span>{location}</span> : null}
          </div>
          <h2>{nameProject}</h2>
          <p>{descProject}</p>
        </div>
      </div>
    );
  }

  // Показываем ошибку
  if (error) {
    return (
      <div className={layoutClass}>
        <div className="cardProject_slider_main">
          <div className="cardProject_Content cardProject_Content--error">
            <span>Ошибка загрузки изображений</span>
          </div>
        </div>
        <div className="text-box">
          <div className="text-box__meta">
            {year ? <span>{year}</span> : null}
            {year && location ? <span>•</span> : null}
            {location ? <span>{location}</span> : null}
          </div>
          <h2>{nameProject}</h2>
          <p>{descProject}</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={layoutClass}
      role="button"
      tabIndex={0}
      onClick={handleCardClick}
      onKeyDown={handleKeyDown}
    >
      <div className="cardProject_slider_main">
        {hasGallery ? (
          <ProjectGallery images={media} title={nameProject} />
        ) : cover ? (
          <div
            className="cardProject_Content"
            style={{ '--media-bg': `url(${cover})` }}
          >
            <img
              src={cover}
              alt={nameProject}
              className="cardProject_Content_img"
              loading="lazy"
              decoding="async"
            />
          </div>
        ) : (
          <div className="cardProject_Content cardProject_Content--placeholder">
            ARCHMIPLAN
          </div>
        )}
      </div>

      <div className="text-box">
        <div className="text-box__meta">
          {year ? <span>{year}</span> : null}
          {year && location ? <span>•</span> : null}
          {location ? <span>{location}</span> : null}
        </div>
        <h2>{nameProject}</h2>
        <p>{descProject}</p>
        {media.length > 0 && (
          <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
            Фотографий: {media.length}
          </div>
        )}
      </div>
    </div>
  );
};

export default CardProject;
